import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useSearchParams } from "react-router";

import { formatDateOnly } from "../clients/clientModel";
import { MonthGrid } from "../calendar/MonthGrid";
import {
  FORMAT_ICONS,
  addMonths,
  monthRange,
  parseMonthKey,
  toMonthKey,
} from "../calendar/pieceModel";
import type { ContentPiece } from "../types";
import { PortalPieceDetail } from "./PortalPieceDetail";

// Home of a client portal user: what is waiting for their approval and the
// month calendar of their brand. RLS limits every query to their company.
export const ClientPortal = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { identity } = useGetIdentity();
  const [searchParams, setSearchParams] = useSearchParams();

  const month = parseMonthKey(searchParams.get("month"));
  const selectedPieceId = searchParams.get("piece");
  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value == null ? next.delete(key) : next.set(key, value),
    );
    setSearchParams(next, { replace: true });
  };

  const { from, to } = monthRange(month);
  const { data: monthPieces = [] } = useGetList<ContentPiece>(
    "content_pieces",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "publish_date", order: "ASC" },
      filter: { "publish_date@gte": from, "publish_date@lte": to },
    },
  );
  const { data: waiting = [] } = useGetList<ContentPiece>("content_pieces", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "publish_date", order: "ASC" },
    filter: { status: "in_review" },
  });

  const firstName = String(identity?.fullName ?? "").split(" ")[0];
  const monthLabel = month.toLocaleDateString(
    locale === "es" ? "es-CO" : locale,
    { month: "long", year: "numeric" },
  );

  return (
    <div className="mt-6 flex flex-col gap-6 pb-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">
          {translate("crm.portal.greeting", { name: firstName })}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {translate("crm.portal.subtitle", {
            company: identity?.companyName ?? "",
          })}
        </p>
      </div>

      {waiting.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <span className="size-2 rounded-full bg-primary" />
            {translate("crm.portal.waiting_title")}
            <span className="text-muted-foreground">{waiting.length}</span>
          </h2>
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
            {waiting.map((piece) => {
              const Icon = FORMAT_ICONS[piece.format];
              return (
                <button
                  key={piece.id}
                  type="button"
                  onClick={() =>
                    updateParams({
                      piece: String(piece.id),
                      month: piece.publish_date
                        ? piece.publish_date.slice(0, 7)
                        : null,
                    })
                  }
                  className="group relative rounded-[22px] bg-primary p-5 text-left text-primary-foreground"
                >
                  <span className="absolute -top-1.5 -right-1.5 grid size-11 place-items-center rounded-full border-[6px] border-background bg-white text-primary">
                    <ArrowUpRight className="size-4" />
                  </span>
                  <span className="flex items-center gap-1.5 text-xs opacity-80">
                    <Icon className="size-3.5" />
                    {translate(
                      `resources.content_pieces.formats.${piece.format}`,
                    )}
                    {piece.publish_date
                      ? ` · ${formatDateOnly(piece.publish_date, locale)}`
                      : ""}
                  </span>
                  <span className="mt-2 block pr-6 text-lg font-semibold leading-snug">
                    {piece.title}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="previous"
            onClick={() =>
              updateParams({ month: toMonthKey(addMonths(month, -1)) })
            }
            className="grid size-10 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label="next"
            onClick={() =>
              updateParams({ month: toMonthKey(addMonths(month, 1)) })
            }
            className="grid size-10 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
        <h2 className="text-2xl font-semibold tracking-tight first-letter:uppercase">
          {monthLabel}
        </h2>
      </div>

      {monthPieces.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.portal.nothing_this_month")}
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <MonthGrid
          month={month}
          pieces={monthPieces}
          selectedPieceId={selectedPieceId}
          onSelect={(pieceId) => updateParams({ piece: String(pieceId) })}
        />
        <PortalPieceDetail pieceId={selectedPieceId} />
      </div>
    </div>
  );
};
